<template>
	<div class="p-p-2 content">
		<TabView v-model:activeIndex="activeTabOption">
			<TabPanel>
				<template #header>
					<i class="mdi mdi-format-list-checks"></i>
					{{ $t('Squeeze.System.Selection') }}
				</template>
				<LanguagesList :loading="loading" :allLanguages="allLanguages" @onChangeActiveStatus="onChangeActiveStatus" @onEntrySelect="onEntrySelect" />
			</TabPanel>
			<TabPanel>
				<template #header>
					<i class="mdi mdi-translate"></i>
					{{ $t('Squeeze.System.Translations') }}
				</template>
				<TranslationListView
					v-if="translationTabLoaded"
					:key="translationListKey"
				/>
			</TabPanel>
			<TabPanel>
				<template #header>
					<i class="mdi mdi-file-import-outline"></i>
					{{ $t('Squeeze.General.Import') }}
				</template>
				<TranslationImportView />
			</TabPanel>
			<TabPanel>
				<template #header>
					<i class="mdi mdi-file-export-outline"></i>
					{{ $t('Squeeze.General.Export') }}
				</template>
				<TranslationExportView />
			</TabPanel>
		</TabView>
	</div>

	<EntryDialog
		:show="showEditDialog"
		@onClose="showEditDialog = false, isValidationInvalid = true, showValidationMessage = false"
		@onConfirm="saveLanguage"
		:loading="loadingDialog"
		:headerText="headerText"
		:showKeepDialogOpen="false"
	>
		<template #content>
			<BlockUI :blocked="loadingDialog">
				<LanguageForm
					:language="language"
					:showErrorMessage="showValidationMessage"
					@update="onUpdate"
				/>
			</BlockUI>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import {ClientManager} from "@/singletons/ClientManager";
import {TranslationCountry} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import BlockUI from "primevue/blockui";
import EntryDialog from "@/components/EntryDialog.vue";
import LanguagesList from "@/apps/administration/components/system/LanguagesList.vue";
import LanguageForm from "@/apps/administration/components/system/LanguageForm.vue";
import TranslationExportView from "@/apps/administration/views/squeeze/system/TranslationExportView.vue";
import TranslationImportView from "@/apps/administration/views/squeeze/system/TranslationImportView.vue";
import TranslationListView from "@/apps/administration/views/squeeze/system/TranslationListView.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import useSqueezeStore from "@/apps/squeeze/store";

export default defineComponent({
	name: "LanguagesView",
	components: {
		LanguagesList,
		LanguageForm,
		TranslationExportView,
		TranslationImportView,
		TranslationListView,
		BlockUI,
		EntryDialog,
		TabPanel,
		TabView,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();

		/** Show Loading on load data */
		const loading = ref<boolean>(false);

		/** Show Loading on Save */
		const loadingDialog = ref(false);

		/** Current active tab option */
		const activeTabOption = ref(0);

		/** Tracks whether the translations tab was opened before its component is mounted */
		const translationTabLoaded = ref<boolean>(false);

		/** Forces the translations tab component to reload when active languages change */
		const translationListKey = ref<number>(0);

		/** Should the Entry-Dialog for translation country be shown? */
		const showEditDialog = ref<boolean>(false);

		/** Is the Form of the users invalid? */
		const isValidationInvalid = ref(true);

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Text of the header in Entry-Dialog */
		const headerText = ref<string>(t('Squeeze.General.ChangeEntry', { entryName: t('Squeeze.System.Language') }));

		/** Api for job management in system */
		const translationsApi = ClientManager.getInstance().squeeze.translations;

		/** All Languages */
		const allLanguages = computed<TranslationCountry[]>(() => store.state.translationCountries);

		/** Entry-Data for translation country */
		const language = reactive<TranslationCountry>({
			countryCode: undefined,
			translatedDescription: undefined,
			translationKey: undefined,
			description: undefined,
			name: undefined,
			active: undefined,
		})

		/** Get all languages */
		const getAllLanguages = () => {
			loading.value = true;
			store.dispatch('fetchTranslationCountries')
				.catch((response: Response) => response.json().then((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				}))
				.finally(() => {
					loading.value = false;
				})
		}

		/** Save current translation country object */
		const saveLanguage = () => {
			loading.value = true;
			loadingDialog.value = true;
			if (language && language.id) {
				translationsApi.putTranslationCountry(language.id, language)
					.then(() => {
						return store.dispatch('fetchTranslationCountries', true);
					})
					.catch((response: Response) => response.json().then ((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}))
					.finally(() => {
						loading.value = false;
						loadingDialog.value = false;
						showEditDialog.value = false;
					})
			}
		}

		/** Triggered when the active status of a translation country is clicked
		 *  @param {TranslationCountry} rowData Row to Save
		 */
		const onChangeActiveStatus = (rowData: TranslationCountry) => {
			Object.assign(language, rowData);
			saveLanguage();
			translationListKey.value += 1;
		}

		/**
		 * Triggered when an entry should be shown
		 * @param rowData Row to change
		 */
		const onEntrySelect = (rowData: TranslationCountry) => {
			Object.assign(language, rowData);
			showEditDialog.value = true;
		}

		/**
		 * Triggered on update of translation country-form
		 * @param data
		 * @param isInvalid
		 */
		const onUpdate = (data: TranslationCountry, isInvalid: boolean) => {
			isValidationInvalid.value = isInvalid;
			Object.assign(language, data);
		}

		onMounted(() => {
			getAllLanguages();
		});

		watch(activeTabOption, (tabIndex: number) => {
			if (tabIndex === 1) {
				translationTabLoaded.value = true;
			}
		});

		return {
			t,
			loading,
			loadingDialog,
			activeTabOption,
			translationTabLoaded,
			translationListKey,
			showEditDialog,
			isValidationInvalid,
			showValidationMessage,
			headerText,
			allLanguages,
			language,
			onChangeActiveStatus,
			saveLanguage,
			onEntrySelect,
			onUpdate,
		};
	},
});

</script>

<style scoped>

.header {
	font-size: 2rem;
	color: var(--dex-primary-color);
	margin: 0;
}

.content {
	height: calc(100vh - 5.6rem);
}

::v-deep(.p-tabview) {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

::v-deep(.p-tabview-panels) {
	flex: 1 1 auto;
	min-height: 0;
	padding: 1rem 0 0 0 !important;
}

::v-deep(.p-tabview-panel) {
	height: 100%;
	min-height: 0;
}

</style>
